import type { components } from '@/generated/contracts/api-v1';

// Domain types for the MaterialRegistry feature (lureo-backend#257).
// Backend responses are camelCase with jsonb columns serialized as strings,
// and Ignore-handling drops null/default-valued keys — the parsers normalise all of that.

// The categories whose instances resolve to a GLB the rows can target by node name
const MATERIAL_ASSET_CATEGORIES = ['character', 'prop', 'mask'] as const;

export function supportsMaterials(category: string | null | undefined): boolean {
  return !!category && (MATERIAL_ASSET_CATEGORIES as readonly string[]).includes(category);
}

/** One flat platform-dialect json per renderer; sparse, absent keys fall through the cascade. */
export interface PlatformProperties {
  unityProperties: Record<string, unknown> | null;
  webProperties: Record<string, unknown> | null;
  webxrProperties: Record<string, unknown> | null;
  swiftProperties: Record<string, unknown> | null;
}

/** Keys every platform applier treats as renderer state, never as shader uniforms. */
export const REGISTRY_PROPERTY_KEYS = new Set([
  'shader',
  'receive_shadows',
  'cast_shadows',
  'render_queue',
  'double_sided',
  'surface_map',
]);

/** A shader_library row: a cross-client look. */
export interface ShaderLook extends PlatformProperties {
  shaderLibraryId: string;
  codeName: string;
  displayName: string | null;
  isActive: boolean;
}

/** A material_types row: a preset on the swatch panel, a live layer in the cascade. */
export interface MaterialPreset extends PlatformProperties {
  materialTypeId: string;
  shaderLibraryId: string;
  displayName: string;
  userId: string | null;
  /** Set when the preset is the material of an asset type rather than a palette swatch. */
  assetTypeId: string | null;
  /** The glTF node names the preset covers within its asset type; null applies to every node. */
  targetNodeNames: string[] | null;
}

export type MaterialInstanceDto = components['schemas']['MaterialInstanceDto'];

/** A material_instances row: a look applied to one node of one asset instance. */
export interface MaterialInstanceRow extends PlatformProperties {
  materialInstanceId: string;
  shaderLibraryId: string;
  materialTypeId: string | null;
  assetInstanceId: string;
  targetNodeName: string | null;
  targetNodeIndex: number | null;
  createdAt: string | null;
  updatedAt: string | null;
}

function parseJson<T>(value: unknown, fallback: T): T {
  if (value == null) return fallback;
  if (typeof value === 'object') return value as T;
  if (typeof value !== 'string' || !value.trim()) return fallback;
  try {
    return JSON.parse(value) as T;
  } catch {
    return fallback;
  }
}

function parsePlatformProperties(raw: Record<string, unknown>): PlatformProperties {
  return {
    unityProperties: parseJson<Record<string, unknown> | null>(raw.unityProperties, null),
    webProperties: parseJson<Record<string, unknown> | null>(raw.webProperties, null),
    webxrProperties: parseJson<Record<string, unknown> | null>(raw.webxrProperties, null),
    swiftProperties: parseJson<Record<string, unknown> | null>(raw.swiftProperties, null),
  };
}

/** The web column of any layer, empty when the layer carries none. */
export function webPropertiesOf(layer: PlatformProperties | null | undefined): Record<string, unknown> {
  return layer?.webProperties ?? {};
}

/** The look for a v1 shader look (listShaderLooks). */
export function shaderLookFromDto(dto: components['schemas']['ShaderLookDto']): ShaderLook {
  return {
    shaderLibraryId: dto.uniqueId,
    codeName: dto.codeName,
    displayName: dto.displayName,
    ...parsePlatformProperties(dto as unknown as Record<string, unknown>),
    isActive: dto.isActive,
  };
}

/** The preset for a v1 material type (listMaterialTypes and its writes). */
export function materialPresetFromDto(dto: components['schemas']['MaterialTypeDto']): MaterialPreset {
  return {
    materialTypeId: dto.uniqueId,
    shaderLibraryId: dto.shaderLookUniqueId,
    displayName: dto.displayName ?? '',
    ...parsePlatformProperties(dto as unknown as Record<string, unknown>),
    userId: dto.userUniqueId,
    assetTypeId: dto.assetTypeUniqueId,
    targetNodeNames: dto.targetNodeNames?.length ? dto.targetNodeNames : null,
  };
}

/** The row for a v1 material instance (listMaterialInstances and its writes). */
export function materialInstanceFromDto(dto: MaterialInstanceDto): MaterialInstanceRow {
  return {
    materialInstanceId: dto.uniqueId,
    shaderLibraryId: dto.shaderLookUniqueId,
    materialTypeId: dto.materialTypeUniqueId,
    assetInstanceId: dto.assetInstanceUniqueId,
    targetNodeName: dto.targetNodeName,
    targetNodeIndex: dto.targetNodeIndex,
    ...parsePlatformProperties(dto as unknown as Record<string, unknown>),
    createdAt: dto.createdAt,
    updatedAt: dto.updatedAt,
  };
}

/** The v1 material instance a row came from, as the renderer bridge carries it. */
export function materialInstanceToDto(row: MaterialInstanceRow): MaterialInstanceDto {
  return {
    uniqueId: row.materialInstanceId,
    shaderLookUniqueId: row.shaderLibraryId,
    materialTypeUniqueId: row.materialTypeId,
    assetInstanceUniqueId: row.assetInstanceId,
    targetNodeName: row.targetNodeName,
    targetNodeIndex: row.targetNodeIndex,
    unityProperties: row.unityProperties,
    webProperties: row.webProperties,
    webxrProperties: row.webxrProperties,
    swiftProperties: row.swiftProperties,
    createdAt: row.createdAt ?? '',
    updatedAt: row.updatedAt,
  };
}

